import React from 'react';
import PhoneUI, { IMessage } from 'src/components/PhoneUI/view';
import { Message } from 'src/constants/types';
import { SendTestMessageButton } from '../SendTestMessage';
import { FormMessage } from '../../types';
import {
  useBuilderConfig, useBuilderKeywords, useBuilderMetadata, useCampaignFormContext, useIsReadOnly,
} from '../../context';
import { isFieldVisible } from '../../helpers';

/** FormMessage → the `Message` shape the shared test-send dialog expects. */
const toMessage = (m: FormMessage): Message => ({
  type: m.type,
  text: m.text ?? '',
  subject: m.subject ?? '',
  links: m.links ?? [],
  linksWithMimeType: m.linksWithMimeType ?? [],
  position: m.position,
  scheduledType: m.scheduledType,
  delay: m.delay,
  time: m.time,
  metadataName: m.metadataName,
  isCaptureContact: m.isCaptureContact,
  dataCapture: m.dataCapture,
  isSurvey: m.isSurvey,
  attachCalendar: m.attachCalendar,
});

export const Phone: React.FC = () => {
  const { values } = useCampaignFormContext();
  const { scheduledMessages = [], keywordIds = [], isOptOutEnabled, optOutText = '' } = values;

  // Both loaded once per builder open by useCampaignConfigLoader, so the preview
  // never refetches.
  const metadata = useBuilderMetadata() ?? undefined;
  const keywords = useBuilderKeywords();
  const isReadOnly = useIsReadOnly();

  // The keyword bubble shows the keyword's NAME, not its id.
  const firstKeywordId = keywordIds[0];
  const firstKeywordName = keywords.find((k) => k.id === firstKeywordId)?.name ?? '';

  // text_to_win campaigns have no scheduled messages — the preview shows the
  // seven prize-draw texts in order instead.
  const { textToWinSettings } = values;
  const config = useBuilderConfig();
  const isTextToWin = config.type === 'text_to_win';

  const messages: IMessage[] = isTextToWin
    ? [
        textToWinSettings?.entrySuccessMessageText,
        textToWinSettings?.entryIntervalLimitMessageText,
        textToWinSettings?.entryTotalLimitMessageText,
        textToWinSettings?.entryNotStartedMessageText,
        textToWinSettings?.entryEndedMessageText,
        textToWinSettings?.winnerMessageText,
        textToWinSettings?.nonWinnerMessageText,
      ]
        .filter(Boolean)
        .map((text, index) => ({ isUser: false, text: text as string, position: index + 1 }))
    : scheduledMessages.map((m) => ({
        isUser: false,
        text: m.text,
        links: m.links,
        linksWithMimeType: m.linksWithMimeType,
        subject: m.subject,
        position: m.position,
      }));

  // When keywords are selected, prepend a user bubble simulating the keyword trigger
  const previewMessages: IMessage[] = isFieldVisible(config.keywords) && firstKeywordId && keywords.length
    ? [{ isUser: true, text: firstKeywordName }, ...messages]
    : messages;

  return (
    <>
      <PhoneUI
        messages={previewMessages}
        metadata={metadata}
        optOutText={isOptOutEnabled ? optOutText : ''}
      />
      <SendTestMessageButton
        disabled={isReadOnly}
        messages={scheduledMessages.map(toMessage)}
        optOutText={isOptOutEnabled ? optOutText : undefined}
        metadata={metadata}
      />
    </>
  );
};
